/**
 * SizilPro — ServiceMenuBuilder
 * Step 5: Build the service catalog.
 *
 * User builds their own services from scratch.
 * No prebuilt suggestions — clean slate approach.
 */

import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Plus,
  GripVertical,
  Trash2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Tag,
  Wrench,
  FileText,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import {
  useServiceMenu,
  useCreateServiceItem,
  useUpdateServiceItem,
  useDeactivateServiceItem,
} from "../../hooks/useServiceMenu";
import type { ServiceItem, PriceUnit } from "../../types";
import { PRICE_UNIT_LABELS } from "../../types";
import {
  SetupLabel,
  SetupInput,
  SetupTextarea,
  SetupSelect,
  SetupActions,
} from "./SetupPrimitives";

type DurationUnit = "hours" | "days" | "weeks" | "months";

const DURATION_UNIT_OPTIONS: { value: DurationUnit; label: string }[] = [
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
];

const optionalNumber = z.preprocess(
  (value) => {
    if (value === "" || value == null) return undefined;
    return Number(value);
  },
  z.number().min(0).optional()
);

const itemSchema = z.object({
  name: z.string().min(1, "Name is required"),
  category: z.string().min(1, "Category is required"),
  default_price: optionalNumber,
  price_unit: z.enum([
    "per_clean",
    "per_hour",
    "per_sqft",
    "per_visit",
    "fixed",
  ]),
  default_square_feet: optionalNumber,
  default_frequency: z.string().optional(),
  default_duration_hours: optionalNumber,
  default_description: z.string().optional(),
});

type ItemFormValues = z.infer<typeof itemSchema>;

const PRICE_UNIT_OPTIONS: { value: PriceUnit; label: string }[] = [
  { value: "per_clean", label: "Per clean" },
  { value: "per_hour", label: "Per hour" },
  { value: "per_sqft", label: "Per square foot" },
  { value: "per_visit", label: "Per visit" },
  { value: "fixed", label: "Fixed price" },
];

interface Props {
  onComplete: () => void;
}

function DurationField({
  register,
  error,
}: {
  register: ReturnType<typeof useForm<ItemFormValues>>["register"];
  error?: string;
}) {
  const [unit, setUnit] = useState<DurationUnit>("hours");
  return (
    <div>
      <SetupLabel>Estimated duration</SetupLabel>
      <div className="flex gap-2">
        <div className="flex-1">
          <SetupInput
            type="number"
            min="0"
            step="0.5"
            placeholder="2"
            error={error}
            {...register("default_duration_hours")}
          />
        </div>
        <div className="w-32">
          <SetupSelect
            value={unit}
            onChange={(e) => setUnit(e.target.value as DurationUnit)}
          >
            {DURATION_UNIT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SetupSelect>
        </div>
      </div>
    </div>
  );
}

function QuestionCard({
  number,
  title,
  description,
  children,
}: {
  number: number;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
      <div className="mb-5 flex items-start gap-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-sm font-semibold text-black">
          {number}
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          {description ? (
            <p className="mt-1 text-sm leading-6 text-white/45">{description}</p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

export function ServiceMenuBuilder({ onComplete }: Props) {
  const { data: items = [], isLoading } = useServiceMenu(false);
  const create = useCreateServiceItem();
  const update = useUpdateServiceItem();
  const deactivate = useDeactivateServiceItem();

  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [clearingAll, setClearingAll] = useState(false);

  async function handleClearAll() {
    if (items.length === 0) return;
    setClearingAll(true);
    try {
      for (const item of items) {
        await deactivate.mutateAsync(item.id);
      }
    } finally {
      setClearingAll(false);
    }
  }

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ItemFormValues>({
    resolver: zodResolver(itemSchema),
    defaultValues: {
      category: "",
      price_unit: "per_clean",
    },
  });

  const addFormPriceUnit = watch("price_unit");
  const addFormPrice     = watch("default_price");
  const addFormSqFt      = watch("default_square_feet");
  const addFormCalc      = addFormPriceUnit === "per_sqft" && addFormPrice && addFormSqFt
    ? addFormPrice * addFormSqFt
    : null;

  const isBusy = create.isPending || update.isPending || deactivate.isPending;

  const onAdd = async (values: ItemFormValues) => {
    await create.mutateAsync({
      ...values,
      name: values.name.trim(),
      category: values.category.trim(),
      default_price: values.default_price ?? null,
      price_unit: values.price_unit,
      default_square_feet: values.price_unit === "per_sqft" ? (values.default_square_feet ?? null) : null,
      default_frequency: values.default_frequency?.trim() || null,
      default_duration_hours: values.default_duration_hours ?? null,
      default_description: values.default_description?.trim() || null,
      sort_order: items.length,
      active: true,
    });

    reset({
      name: "",
      category: "",
      price_unit: "per_clean",
      default_price: undefined,
      default_square_feet: undefined,
      default_frequency: "",
      default_duration_hours: undefined,
      default_description: "",
    });
    setShowAddForm(false);
  };

  // Group items by their raw category string
  const groupedItems = items.reduce<Record<string, ServiceItem[]>>((acc, item) => {
    const category = item.category?.trim() || "Uncategorized";
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="mb-2">
        <p className="text-sm leading-6 text-white/55">
          Build the service foundation your templates and proposals will pull from.
          These become the starting point for scope, pricing style, and AI-generated proposal copy.
        </p>
      </div>

      <div className="rounded-2xl border border-amber-500/15 bg-amber-500/5 p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div>
            <p className="text-sm font-medium text-amber-300">
              These become your reusable default services
            </p>
            <p className="mt-1 text-sm leading-6 text-white/45">
              Brand Setup is where you define the service library once. Templates
              choose which default services should be included, and proposals inherit those services
              as their starting point.
            </p>
          </div>
        </div>
      </div>

      <QuestionCard
        number={1}
        title="Build your service catalog"
        description="Add each service you offer. Give it a name, category, pricing, and an optional scope description."
      >
        <div className="space-y-4">
          {/* Add service toggle */}
          <div className="overflow-hidden rounded-xl border border-white/10">
            <button
              type="button"
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex w-full items-center justify-between px-5 py-3.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/5 hover:text-white"
            >
              <span className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add a service
              </span>

              {showAddForm ? (
                <ChevronUp className="h-4 w-4 text-white/30" />
              ) : (
                <ChevronDown className="h-4 w-4 text-white/30" />
              )}
            </button>

            {showAddForm && (
              <form
                onSubmit={handleSubmit(onAdd)}
                className="space-y-4 border-t border-white/10 px-5 pb-5 pt-2"
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <SetupLabel required>Service name</SetupLabel>
                    <SetupInput
                      placeholder="Window Cleaning"
                      error={errors.name?.message}
                      {...register("name")}
                    />
                  </div>

                  <div>
                    <SetupLabel required>Category</SetupLabel>
                    <SetupInput
                      placeholder="e.g. Residential, Commercial, Post-Construction..."
                      error={errors.category?.message}
                      {...register("category")}
                    />
                  </div>

                  <div>
                    <SetupLabel>Default frequency</SetupLabel>
                    <SetupInput
                      placeholder="Daily, Weekly, Monthly, One-time..."
                      {...register("default_frequency")}
                    />
                  </div>

                  <div>
                    <SetupLabel>Default price</SetupLabel>
                    <SetupInput
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="250.00"
                      {...register("default_price")}
                    />
                  </div>

                  <div>
                    <SetupLabel>Priced by</SetupLabel>
                    <SetupSelect {...register("price_unit")}>
                      {PRICE_UNIT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </SetupSelect>
                  </div>

                  {addFormPriceUnit === "per_sqft" && (
                    <div className="md:col-span-2">
                      <SetupLabel>Default square footage</SetupLabel>
                      <SetupInput
                        type="number"
                        min="0"
                        step="1"
                        placeholder="2000"
                        error={errors.default_square_feet?.message}
                        {...register("default_square_feet")}
                      />
                      {addFormCalc != null && (
                        <p className="mt-1.5 text-xs text-white/45">
                          {Number(addFormSqFt).toLocaleString()} sq ft × ${Number(addFormPrice).toFixed(2)}/sqft
                          {" "}= <span className="font-semibold text-white/70">${addFormCalc.toFixed(2)}</span>
                        </p>
                      )}
                    </div>
                  )}

                  <DurationField
                    register={register}
                    error={errors.default_duration_hours?.message}
                  />
                </div>

                <div>
                  <SetupLabel>Default scope description (optional)</SetupLabel>
                  <p className="mb-2 text-xs text-white/30">
                    Leave this blank if you want AI to help draft scope wording later.
                  </p>

                  <SetupTextarea
                    rows={4}
                    placeholder="Sweep and mop all floor surfaces. Sanitize tables, counters, touchpoints, and shared areas."
                    {...register("default_description")}
                  />
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <button
                    type="submit"
                    disabled={create.isPending}
                    className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium transition-colors hover:bg-white/15 disabled:opacity-40"
                  >
                    {create.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Plus className="h-3.5 w-3.5" />
                    )}
                    Add to catalog
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="text-sm text-white/30 transition-colors hover:text-white/50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Current catalog */}
          {items.length > 0 && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClearAll}
                disabled={clearingAll}
                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-red-400/70 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-40"
              >
                {clearingAll ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
                {clearingAll ? "Clearing…" : "Remove all services"}
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-white/30" />
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-white/45">
              No services yet — add your first one above.
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedItems).map(([category, categoryItems]) => (
                <div key={category} className="space-y-2">
                  <div className="flex items-center gap-2 px-1">
                    <Tag className="h-3 w-3 text-white/30" />
                    <span className="text-[11px] uppercase tracking-[0.15em] text-white/30">
                      {category}
                    </span>
                  </div>

                  {categoryItems.map((item) => (
                    <ServiceItemRow
                      key={item.id}
                      item={item}
                      expanded={expandedId === item.id}
                      isSaving={update.isPending && update.variables?.id === item.id}
                      isDeleting={
                        deactivate.isPending && deactivate.variables === item.id
                      }
                      onToggle={() =>
                        setExpandedId(expandedId === item.id ? null : item.id)
                      }
                      onSave={(updates) => update.mutate({ id: item.id, updates })}
                      onDelete={() => deactivate.mutate(item.id)}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </QuestionCard>

      <QuestionCard
        number={2}
        title="How these services get used later"
        description="This is the handoff from setup into the rest of Proposal Builder."
      >
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-white/10 bg-black/20 p-4">
            <Wrench className="h-4 w-4 text-white/35" />
            <p className="mt-3 text-sm font-medium text-white/80">Brand Setup</p>
            <p className="mt-1 text-xs leading-6 text-white/40">
              This is where your full reusable service catalog lives.
            </p>
          </div>

          <div className="rounded-xl border border-white/10 bg-black/20 p-4">
            <FileText className="h-4 w-4 text-white/35" />
            <p className="mt-3 text-sm font-medium text-white/80">Templates</p>
            <p className="mt-1 text-xs leading-6 text-white/40">
              Templates choose which default services should be included for a job type.
            </p>
          </div>

          <div className="rounded-xl border border-white/10 bg-black/20 p-4">
            <ArrowRight className="h-4 w-4 text-white/35" />
            <p className="mt-3 text-sm font-medium text-white/80">Proposals</p>
            <p className="mt-1 text-xs leading-6 text-white/40">
              Final proposals inherit those services and can then be edited job by job.
            </p>
          </div>
        </div>
      </QuestionCard>

      <SetupActions>
        <button
          type="button"
          onClick={onComplete}
          disabled={isBusy}
          className="rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
        >
          {items.length === 0 ? "Skip for now" : "Save & continue"}
        </button>
      </SetupActions>
    </div>
  );
}

function ServiceItemRow({
  item,
  expanded,
  isSaving,
  isDeleting,
  onToggle,
  onSave,
  onDelete,
}: {
  item: ServiceItem;
  expanded: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onToggle: () => void;
  onSave: (updates: Partial<ServiceItem>) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(item.name);
  const [category, setCategory] = useState(item.category ?? "");
  const [price, setPrice] = useState(String(item.default_price ?? ""));
  const [priceUnit, setPriceUnit] = useState<PriceUnit>(item.price_unit);
  const [squareFeet, setSquareFeet] = useState(String(item.default_square_feet ?? ""));
  const [frequency, setFrequency] = useState(item.default_frequency ?? "");
  const [duration, setDuration] = useState(String(item.default_duration_hours ?? ""));
  const [description, setDescription] = useState(item.default_description ?? "");
  const [dirty, setDirty] = useState(false);

  const sqFtCalc = useCallback(() => {
    const r = parseFloat(price);
    const s = parseFloat(squareFeet);
    return priceUnit === "per_sqft" && Number.isFinite(r) && Number.isFinite(s) && s > 0
      ? r * s
      : null;
  }, [price, priceUnit, squareFeet]);

  useEffect(() => {
    setName(item.name);
    setCategory(item.category ?? "");
    setPrice(String(item.default_price ?? ""));
    setPriceUnit(item.price_unit);
    setSquareFeet(String(item.default_square_feet ?? ""));
    setFrequency(item.default_frequency ?? "");
    setDuration(String(item.default_duration_hours ?? ""));
    setDescription(item.default_description ?? "");
    setDirty(false);
  }, [
    item.name,
    item.category,
    item.default_price,
    item.price_unit,
    item.default_square_feet,
    item.default_frequency,
    item.default_duration_hours,
    item.default_description,
  ]);

  const handleSave = () => {
    onSave({
      name: name.trim(),
      category: category.trim() || null,
      default_price: price.trim() === "" ? null : parseFloat(price),
      price_unit: priceUnit,
      default_square_feet: priceUnit === "per_sqft" && squareFeet.trim() !== ""
        ? parseFloat(squareFeet)
        : null,
      default_frequency: frequency.trim() === "" ? null : frequency.trim(),
      default_duration_hours:
        duration.trim() === "" ? null : parseFloat(duration),
      default_description:
        description.trim() === "" ? null : description.trim(),
    });
  };

  return (
    <div className="overflow-hidden rounded-xl border border-white/10">
      <div className="flex items-center gap-3 px-4 py-3">
        <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-white/15" />

        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center justify-between text-left"
        >
          <div className="min-w-0">
            <span className="text-sm font-medium">{item.name}</span>
            {item.category && (
              <span className="ml-2 text-xs text-white/35">{item.category}</span>
            )}
            {item.default_price != null && (
              <span className="ml-2 text-xs text-white/35">
                {item.price_unit === "per_sqft" && item.default_square_feet != null
                  ? `${item.default_square_feet.toLocaleString()} sq ft × $${item.default_price}/sqft = $${(item.default_price * item.default_square_feet).toFixed(2)}`
                  : `$${item.default_price} ${PRICE_UNIT_LABELS[item.price_unit]}`}
              </span>
            )}
          </div>

          {expanded ? (
            <ChevronUp className="h-4 w-4 text-white/30" />
          ) : (
            <ChevronDown className="h-4 w-4 text-white/30" />
          )}
        </button>

        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          className="shrink-0 rounded-md p-1.5 text-white/15 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
        >
          {isDeleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-white/10 px-4 pb-4 pt-3">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <SetupLabel>Name</SetupLabel>
              <SetupInput
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setDirty(true);
                }}
              />
            </div>

            <div>
              <SetupLabel>Category</SetupLabel>
              <SetupInput
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setDirty(true);
                }}
                placeholder="e.g. Residential, Commercial..."
              />
            </div>

            <div>
              <SetupLabel>Default frequency</SetupLabel>
              <SetupInput
                value={frequency}
                onChange={(e) => {
                  setFrequency(e.target.value);
                  setDirty(true);
                }}
                placeholder="Weekly"
              />
            </div>

            <div>
              <SetupLabel>Default price</SetupLabel>
              <SetupInput
                type="number"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  setDirty(true);
                }}
                placeholder="250.00"
              />
            </div>

            <div>
              <SetupLabel>Priced by</SetupLabel>
              <SetupSelect
                value={priceUnit}
                onChange={(e) => {
                  setPriceUnit(e.target.value as PriceUnit);
                  setDirty(true);
                }}
              >
                {PRICE_UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SetupSelect>
            </div>

            {priceUnit === "per_sqft" && (
              <div className="md:col-span-2">
                <SetupLabel>Default square footage</SetupLabel>
                <SetupInput
                  type="number"
                  min="0"
                  step="1"
                  value={squareFeet}
                  placeholder="2000"
                  onChange={(e) => {
                    setSquareFeet(e.target.value);
                    setDirty(true);
                  }}
                />
                {sqFtCalc() != null && (
                  <p className="mt-1.5 text-xs text-white/45">
                    {Number(squareFeet).toLocaleString()} sq ft × ${parseFloat(price).toFixed(2)}/sqft
                    {" "}= <span className="font-semibold text-white/70">${sqFtCalc()!.toFixed(2)}</span>
                  </p>
                )}
              </div>
            )}

            <div>
              <SetupLabel>Estimated duration (hours)</SetupLabel>
              <SetupInput
                type="number"
                value={duration}
                onChange={(e) => {
                  setDuration(e.target.value);
                  setDirty(true);
                }}
                placeholder="2"
              />
            </div>
          </div>

          <div>
            <SetupLabel>Default scope description</SetupLabel>
            <SetupTextarea
              rows={4}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setDirty(true);
              }}
              placeholder="Describe the normal scope for this service."
            />
          </div>

          {dirty && (
            <button
              type="button"
              onClick={handleSave}
              disabled={
                isSaving ||
                !name.trim() ||
                (price.trim() !== "" && Number.isNaN(parseFloat(price))) ||
                (duration.trim() !== "" && Number.isNaN(parseFloat(duration)))
              }
              className="rounded-lg bg-white/10 px-3 py-1.5 text-xs transition-colors hover:bg-white/15 disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save changes"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
